import React from "react";
import { createPortal } from "react-dom";
import {
  FiAlertTriangle,
  FiCheckCircle,
  FiInfo,
  FiX,
  FiXCircle,
} from "react-icons/fi";

export type PayoutNotificationTone = "info" | "success" | "error" | "warning";

type PayoutNotificationPayload = {
  tone?: PayoutNotificationTone;
  title?: string;
  message: string;
};

type ActiveNotification = PayoutNotificationPayload & {
  id: number;
  tone: PayoutNotificationTone;
};

export const payoutNotificationEvent = "payout:notification";

const toneConfig: Record<
  PayoutNotificationTone,
  {
    title: string;
    icon: React.ReactNode;
    stripe: string;
    background: string;
    titleColor: string;
    messageColor: string;
    closeColor: string;
  }
> = {
  info: {
    title: "Heads up!",
    icon: <FiInfo />,
    stripe: "#2F6BFF",
    background: "#F8FBFF",
    titleColor: "#0F57D8",
    messageColor: "#1553B7",
    closeColor: "#0F57D8",
  },
  success: {
    title: "Success!",
    icon: <FiCheckCircle />,
    stripe: "#10A37F",
    background: "#EDFCF6",
    titleColor: "#047857",
    messageColor: "#047857",
    closeColor: "#047857",
  },
  error: {
    title: "Error!",
    icon: <FiXCircle />,
    stripe: "#DC2626",
    background: "#FEF2F2",
    titleColor: "#B91C1C",
    messageColor: "#B91C1C",
    closeColor: "#B91C1C",
  },
  warning: {
    title: "Warning!",
    icon: <FiAlertTriangle />,
    stripe: "#D97706",
    background: "#FFFBEB",
    titleColor: "#B45309",
    messageColor: "#B45309",
    closeColor: "#B45309",
  },
};

export const showPayoutNotification = ({
  tone = "info",
  title,
  message,
}: PayoutNotificationPayload) => {
  if (!message.trim()) return;
  window.dispatchEvent(
    new CustomEvent<PayoutNotificationPayload>(payoutNotificationEvent, {
      detail: { tone, title, message },
    }),
  );
};

function PayoutNotification() {
  const [notification, setNotification] =
    React.useState<ActiveNotification | null>(null);

  React.useEffect(() => {
    const handleNotification = (event: Event) => {
      const detail = (event as CustomEvent<PayoutNotificationPayload>).detail;
      if (!detail?.message) return;
      setNotification({
        id: Date.now(),
        tone: detail.tone || "info",
        title: detail.title,
        message: detail.message,
      });
    };

    window.addEventListener(payoutNotificationEvent, handleNotification);
    return () => {
      window.removeEventListener(payoutNotificationEvent, handleNotification);
    };
  }, []);

  React.useEffect(() => {
    if (!notification) return undefined;
    const timer = window.setTimeout(() => setNotification(null), 6000);
    return () => window.clearTimeout(timer);
  }, [notification]);

  if (!notification) return null;

  const config = toneConfig[notification.tone];

  return createPortal(
    <div
      className="pointer-events-none fixed left-1/2 top-7 w-[min(680px,calc(100vw-2rem))] -translate-x-1/2"
      style={{ zIndex: 2147483647 }}
    >
      <div
        className="pointer-events-auto flex min-h-[58px] items-center gap-3 border-l-[3px] px-4 py-3 shadow-[0_14px_34px_rgba(27,22,59,0.12)]"
        style={{
          borderLeftColor: config.stripe,
          backgroundColor: config.background,
        }}
        role="alert"
      >
        <span
          className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-xs text-white"
          style={{ backgroundColor: config.stripe }}
        >
          {config.icon}
        </span>
        <div className="min-w-0 flex-1">
          <p
            className="text-sm font-semibold leading-4"
            style={{ color: config.titleColor }}
          >
            {notification.title || config.title}
          </p>
          <p
            className="mt-0.5 text-xs font-medium leading-4"
            style={{ color: config.messageColor }}
          >
            {notification.message}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setNotification(null)}
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[5px] border border-current bg-white text-sm"
          style={{ color: config.closeColor }}
          aria-label="Close notification"
          title="Close"
        >
          <FiX />
        </button>
      </div>
    </div>,
    document.body,
  );
}

export default PayoutNotification;
